import { Shield, Lock, Eye, FileCheck, Server } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const items = [
  {
    icon: Lock,
    title: "Ephemeral processing",
    description: "AI processing is short-lived and not retained after the operation completes.",
  },
  {
    icon: Shield,
    title: "Direct to Microsoft",
    description: "Contacts are created in your Outlook account via the Microsoft Graph API. We don't store contact data.",
  },
  {
    icon: Eye,
    title: "Full transparency",
    description: "You see every extracted field before saving. Optional features work on metadata you approve.",
  },
  {
    icon: Server,
    title: "EU-hosted AI",
    description: "Processing on EU-based servers with zero data retention. Built with Privacy by Design.",
  },
];

export const Privacy = () => {
  return (
    <section className="py-20 md:py-28 bg-surface-1 relative overflow-hidden">
      <div className="container px-4">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20 items-center">
          {/* Visual */}
          <div className="relative max-w-md mx-auto lg:mx-0">
            <div className="relative aspect-square fluent-tile bg-card p-8 md:p-12 grid place-items-center">
              <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true">
                {/* Outer ring */}
                <circle cx="100" cy="100" r="92" fill="none" stroke="hsl(var(--border))" strokeWidth="1" />
                <circle cx="100" cy="100" r="74" fill="none" stroke="hsl(var(--border))" strokeWidth="1" strokeDasharray="2 4" />
                {/* Lock body */}
                <rect x="65" y="92" width="70" height="55" rx="8" fill="hsl(var(--surface-2))" stroke="hsl(var(--border))" strokeWidth="1.5" />
                {/* Shackle */}
                <path d="M 78 92 V 75 a 22 22 0 0 1 44 0 V 92" fill="none" stroke="hsl(var(--foreground))" strokeWidth="4" strokeLinecap="round" />
                {/* Keyhole */}
                <circle cx="100" cy="115" r="6" fill="hsl(var(--primary))" />
                <rect x="97" y="118" width="6" height="14" rx="2" fill="hsl(var(--primary))" />
                {/* GDPR mark */}
                <text x="100" y="172" textAnchor="middle" className="font-mono" fontSize="9" fill="hsl(var(--muted-foreground))" letterSpacing="2">
                  GDPR · EU · 2018
                </text>
              </svg>
            </div>
            {/* Floating compliance chips */}
            <div className="hidden md:flex absolute -top-3 -left-3 items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card border border-border shadow-fluent-8">
              <Shield className="w-3.5 h-3.5 text-success" />
              <span className="text-xs font-mono uppercase tracking-wider">GDPR</span>
            </div>
            <div className="hidden md:flex absolute -bottom-3 -right-3 items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card border border-border shadow-fluent-8">
              <FileCheck className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-mono uppercase tracking-wider">Auditable</span>
            </div>
          </div>

          {/* Content */}
          <div>
            <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
              Privacy by design
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold mt-3 mb-5">
              Privacy-first. Built for GDPR.
            </h2>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8">
              All AI processing happens in EU data centers with zero data retention. You control what gets processed.
            </p>

            <ul className="space-y-4 mb-8">
              {items.map((item) => (
                <li key={item.title} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-md bg-card border border-border grid place-items-center flex-shrink-0 mt-0.5">
                    <item.icon className="w-4 h-4 text-foreground" strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-sm mb-0.5">{item.title}</div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                  </div>
                </li>
              ))}
            </ul>

            <Button asChild variant="outline">
              <Link to="/privacy">Read our privacy standards</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
